import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ticket, RefreshCw, ChevronDown, ChevronRight, Loader2, RotateCcw, CircleCheck, Clock, Inbox } from 'lucide-react';
import { Card } from '../ui/Card.jsx';
import { dealerApi } from '../../api/dealerDiagnose.js';

/**
 * 經銷商「我的回報」— 診斷助手開出去的工單清單與進度。
 * 狀態（經銷商視角）：
 *   open（AI 未接手）        → 已回報
 *   open + aiStatus=processing → 處理中（AI 工程助理已接手分析 / 等 Qubby 團隊）
 *   resolved                  → 已處理（可「仍有問題，重開」→ 回到已回報，AI 會重新分診）
 */
const WEEK_MS = 7 * 24 * 3600 * 1000;
const OLDER_PAGE_SIZE = 10;
const tsMs = (ts) => (ts?._seconds ? ts._seconds * 1000 : (ts?.toMillis ? ts.toMillis() : ts)) || 0;

export default function DealerTicketsPanel() {
    const { t } = useTranslation('dealer');
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(false);
    const [busy, setBusy] = useState(null);          // reopen 中的 ticketId
    const [confirming, setConfirming] = useState(null);
    const [page, setPage] = useState(1);             // 第 1 頁=近 7 天；之後每頁 10 筆較早的

    const load = useCallback(() => {
        setLoading(true);
        dealerApi.myTickets()
            .then((r) => setTickets(r?.tickets || []))
            .catch(() => setTickets([]))
            .finally(() => setLoading(false));
    }, []);
    useEffect(load, [load]);

    const reopen = async (ticketId) => {
        if (confirming !== ticketId) {
            setConfirming(ticketId);
            setTimeout(() => setConfirming((c) => (c === ticketId ? null : c)), 4000);
            return;
        }
        setConfirming(null);
        setBusy(ticketId);
        try { await dealerApi.reopenTicket(ticketId); load(); }
        catch (e) { alert(t('tickets.reopenFailed', { reason: e.message })); }
        finally { setBusy(null); }
    };

    const fmtTime = (ts) => {
        const ms = ts?._seconds ? ts._seconds * 1000 : (ts?.toMillis ? ts.toMillis() : ts);
        const d = ms ? new Date(ms) : null;
        return d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : '—';
    };

    // 經銷商視角三態
    const stateOf = (tk) => (tk.status === 'resolved' ? 'done' : tk.aiStatus === 'processing' ? 'working' : 'reported');
    const STATE_UI = {
        reported: { icon: Inbox, cls: 'bg-cream-100 text-ink-600', label: t('tickets.stateReported') },
        working: { icon: Clock, cls: 'bg-amber-100 text-amber-700', label: t('tickets.stateWorking') },
        done: { icon: CircleCheck, cls: 'bg-success-50 text-success-700', label: t('tickets.stateDone') },
    };

    if (!loading && tickets.length === 0) return null;   // 沒回報過 → 整區不顯示

    // 分頁：第 1 頁=近 7 天（全部）；較早的每頁 OLDER_PAGE_SIZE 筆
    const now = Date.now();
    const recent = tickets.filter((tk) => now - tsMs(tk.createdAt) <= WEEK_MS);
    const older = tickets.filter((tk) => now - tsMs(tk.createdAt) > WEEK_MS);
    const totalPages = 1 + Math.ceil(older.length / OLDER_PAGE_SIZE);
    const safePage = Math.min(page, totalPages);
    const visible = safePage === 1 ? recent : older.slice((safePage - 2) * OLDER_PAGE_SIZE, (safePage - 1) * OLDER_PAGE_SIZE);

    return (
        <Card padded={false}>
            <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                className="w-full px-4 py-2.5 flex items-center gap-2 text-left"
            >
                {collapsed ? <ChevronRight size={14} className="text-ink-400" /> : <ChevronDown size={14} className="text-ink-400" />}
                <Ticket size={15} className="text-accent-500" />
                <span className="text-sm font-semibold text-ink-900">{t('tickets.title')}</span>
                <span className="text-[11px] text-ink-400">
                    {t('tickets.count', { open: tickets.filter((tk) => tk.status === 'open').length, total: tickets.length })}
                </span>
                <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); load(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); load(); } }}
                    className="ml-auto inline-flex items-center gap-1 text-xs text-ink-500 hover:text-accent-700"
                >
                    <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                </span>
            </button>
            {!collapsed && (
                <div className="border-t border-cream-200 divide-y divide-cream-100 max-h-64 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center gap-2 text-sm text-ink-400 p-4"><Loader2 size={14} className="animate-spin" /> {t('tickets.loading')}</div>
                    ) : visible.length === 0 ? (
                        <div className="p-4 text-xs text-ink-400">{t('tickets.emptyWeek')}</div>
                    ) : visible.map((tk) => {
                        const st = STATE_UI[stateOf(tk)];
                        const Icon = st.icon;
                        return (
                            <div key={tk.ticketId} className="px-4 py-2.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-xs font-semibold text-accent-700">{tk.ticketId}</span>
                                    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${st.cls}`}>
                                        <Icon size={10} /> {st.label}
                                    </span>
                                    {tk.avatarName && <span className="text-[11px] text-ink-500">{tk.avatarName}</span>}
                                    {tk.reopenCount > 0 && <span className="font-mono text-[10px] text-warn-600">↻{tk.reopenCount}</span>}
                                    <span className="ml-auto text-[11px] text-ink-400">{fmtTime(tk.createdAt)}</span>
                                    {tk.status === 'resolved' && (
                                        <button
                                            onClick={() => reopen(tk.ticketId)}
                                            disabled={busy === tk.ticketId}
                                            className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition-colors disabled:opacity-40
                                                ${confirming === tk.ticketId
                                                    ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                                                    : 'border-cream-300 text-ink-600 hover:bg-cream-100'}`}
                                        >
                                            {busy === tk.ticketId ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                                            {confirming === tk.ticketId ? t('tickets.reopenConfirm') : t('tickets.reopen')}
                                        </button>
                                    )}
                                </div>
                                <div className="mt-1 text-xs text-ink-600 line-clamp-2">{tk.dealerFacingSummary || '—'}</div>
                                {tk.dealerProgress && (
                                    <div className="mt-1 text-[11px] text-ink-500">
                                        <span className="text-ink-400">{t('tickets.progress')}</span> {tk.dealerProgress}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {!collapsed && !loading && totalPages > 1 && (
                <div className="border-t border-cream-200 px-4 py-1.5 flex items-center justify-end gap-1">
                    <span className="mr-auto text-[10px] text-ink-400">
                        {safePage === 1 ? t('tickets.pageRecent') : t('tickets.pageOlder', { page: safePage - 1 })}
                    </span>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <button
                            key={p}
                            onClick={() => setPage(p)}
                            className={`min-w-6 rounded px-1.5 py-0.5 text-[11px] font-mono transition-colors
                                ${p === safePage ? 'bg-accent-600 text-white' : 'text-ink-500 hover:bg-cream-100'}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            )}
        </Card>
    );
}
